import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import { FormGroupControl } from '@components/Base';
import { HealthClaimBillingItem } from '@models/HealthClaimBillingItem';
import { healthClaimBillingItemUpdate } from '../action';
import { billingItemEditSchema, BillingItemEditFormValues } from './billing-item-schema';

const GROUP_PROPS = { className: 'mb-3' };

type Props = {
  show: boolean;
  onHide: () => void;
  healthClaimBillingItem: HealthClaimBillingItem | null;
};

const BillingItemEditModal = ({ show, onHide, healthClaimBillingItem }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  if (!healthClaimBillingItem) return null;

  const initialValues: BillingItemEditFormValues = {
    priceAmount: Number(healthClaimBillingItem.priceAmount),
    quantity: healthClaimBillingItem.quantity,
    discountPercentage: Number(healthClaimBillingItem.discountPercentage),
  };

  const handleSubmit = (values: BillingItemEditFormValues) => {
    dispatch(
      healthClaimBillingItemUpdate({
        id: healthClaimBillingItem.id,
        priceAmount: `${values.priceAmount}`,
        quantity: values.quantity,
        discountPercentage: `${values.discountPercentage}`,
      })
    );
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Formik
        initialValues={initialValues}
        validationSchema={billingItemEditSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Modal.Header closeButton>
              <Modal.Title>{t('billingDetails.editItem')}: {healthClaimBillingItem.itemName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <FormGroupControl
                required
                name="priceAmount"
                label={t('billingDetails.price')}
                type="number"
                groupProps={GROUP_PROPS}
                asRow
              />
              <FormGroupControl
                required
                name="quantity"
                label={t('billingDetails.quantity')}
                type="number"
                groupProps={GROUP_PROPS}
                asRow
              />
              <FormGroupControl
                required
                name="discountPercentage"
                label={t('billingDetails.discount')}
                type="number"
                groupProps={GROUP_PROPS}
                asRow
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onHide}>
                {t('confirmModal.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {t('common:submit')}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export { BillingItemEditModal };
